import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { useGuestMode } from '../hooks/useGuestMode';
import { AppHeader } from '../components/AppHeader';
import { useAppModal } from '../components/Modal';
import { GameGrid } from '../components/GameGrid';
import { Numpad } from '../components/Numpad';
import { SettingsPopup, type SettingToggle } from '../components/SettingsPopup';
import { FontSizeToggle } from '../components/FontSizeToggle';
import { Confetti } from '../components/Confetti';
import { ComboCounter } from '../components/ComboCounter';
import { GameStatusBar } from '../components/GameStatusBar';
import { DIFF_COLORS } from '../theme/difficulty';
import { techniqueSlug, techniqueLevel, explainStep as explainStepText } from '@sudoku/shared';
import { useGameBoard, formatTime, isGiven, clearAutoNotes, toggleNoteLocal } from '../hooks/useGameBoard';
import { useGameEffects } from '../hooks/useGameEffects';
import { AchievementToast } from '../components/AchievementToast';
import { RatingDeltaToast } from '../components/RatingDeltaToast';
import { LevelBadge } from '../components/LevelBadge';
import { useScrollLock } from '../hooks/useScrollLock';
import { AdBanner, AD_SLOTS } from '../components/AdBanner';
import './GamePage.css';
import './RoomPage.css';

const SOCKET_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface PlayerInfo {
  nickname: string;
  progress: number;
  mistakes: number;
  correct_count?: number;
  wrong_count?: number;
  is_finished: boolean;
  is_failed?: boolean;
  elapsed: number;
  color: string;
  score: number;
  hints_used?: number;
  finish_bonus?: number;
  solver_used?: boolean;
  rating?: number;
}

/** localStorage'dan player_id al, yoksa üret */
function getOrCreatePlayerId(): string {
  let id = localStorage.getItem('sudoku_player_id');
  if (!id) {
    id = 'p_' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sudoku_player_id', id);
  }
  return id;
}



export function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { user, token } = useAuth();
  const { isGuest } = useGuestMode();
  const isGuestUser = !user && isGuest;
  const [friendRequestState, setFriendRequestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [friendRequestMsg, setFriendRequestMsg] = useState('');
  const [friendNicks, setFriendNicks] = useState<Set<string>>(new Set());
  const [newAchievements, setNewAchievements] = useState<string[]>([]);
  const [ratingUpdate, setRatingUpdate] = useState<{ delta: number; old_rating: number; new_rating: number } | null>(null);
  const [xpInfo, setXpInfo] = useState<{ delta: number; total_xp: number; old_level: number; new_level: number; leveled_up: boolean; xp_into_level: number; xp_for_next: number } | null>(null);
  const [combo, setCombo] = useState(0);
  const { showAlert, showConfirm, showChoice, modalElement } = useAppModal();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [eliminatedToast, setEliminatedToast] = useState(false);
  const [finishedToast, setFinishedToast] = useState(false);

  // Player identity — giriş yapmışsa user.id, aksi halde localStorage random ID
  const [playerId] = useState(() => user?.id || getOrCreatePlayerId());
  const [nickname, setNickname] = useState(() => user?.nickname || localStorage.getItem('sudoku_nickname') || '');
  const [nicknameConfirmed, setNicknameConfirmed] = useState(() => !!user?.nickname || !!localStorage.getItem('sudoku_nickname'));
  const nicknameRef = useRef(nickname);
  nicknameRef.current = nickname;

  // Giriş yapılmışsa kullanıcı adını otomatik kullan
  useEffect(() => {
    if (user?.nickname) {
      setNickname(user.nickname);
      setNicknameConfirmed(true);
    }
  }, [user?.nickname]);

  // Arkadaş listesini çek — oyun sonu ekranında zaten arkadaş olanlar için ekle butonu gizlenecek
  useEffect(() => {
    if (!token) { setFriendNicks(new Set()); return; }
    let aborted = false;
    fetch(`${SOCKET_URL}/friends`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (aborted || !data?.friends) return;
        setFriendNicks(new Set(data.friends.map((f: { nickname: string }) => f.nickname)));
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [token]);

  // Room state
  const [roomCode, setRoomCode] = useState('');
  const [roomName, setRoomName] = useState('');
  const [roomType, setRoomType] = useState('friend');
  const [hostId, setHostId] = useState('');
  const [difficulty, setDifficulty] = useState(3);
  const [status, setStatus] = useState<'waiting' | 'playing' | 'finished'>('waiting');
  const [players, setPlayers] = useState<Record<string, PlayerInfo>>({});
  const [, setLeaderboard] = useState<Array<{ nickname: string; elapsed: number }>>([]);
  const [solution, setSolution] = useState<(number | null)[][]>([]);
  const [startingGame, setStartingGame] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [restartDifficulty, setRestartDifficulty] = useState<number | null>(null);
  const statusRef = useRef<'waiting' | 'playing' | 'finished'>('waiting');

  // Callback ref pattern — hook'un keyboard handler'ı için
  const placeValueRef = useRef<(r: number, c: number, value: number) => void>(() => {});
  const eraseRef = useRef<(r: number, c: number) => void>(() => {});

  const myState = players[playerId];
  const isHost = playerId === hostId;
  const isRankedRoom = roomType === 'ranked';
  const opponent = Object.entries(players).find(([pid]) => pid !== playerId)?.[1];

  const gb = useGameBoard({
    isPlaying: status === 'playing' && !myState?.is_finished,
    onPlace: (r, c, v) => placeValueRef.current(r, c, v),
    onErase: (r, c) => eraseRef.current(r, c),
  });

  useScrollLock();

  const userRef = useRef(user);
  const [codeCopied, setCodeCopied] = useState(false);
  const [showColors, setShowColors] = useState(true);
  const [showRivalDots, setShowRivalDots] = useState(true);

  // Solver kilidi: oyuncu Analiz Et'e basınca bulmacayı çözemez
  const [usedSolver, setUsedSolver] = useState(() =>
    sessionStorage.getItem(`solver_used_${roomId}`) === '1'
  );
  const fx = useGameEffects();
  // Oda ayarları (kurucu tarafından belirlenir)
  const [allowHints, setAllowHints] = useState(true);
  const [allowCandidates, setAllowCandidates] = useState(true);
  // Rakip hint: key="r-c" → pid. Rakip orada çözdü, biz çözmedik → renkli boş hücre göster
  const [rivalHints, setRivalHints] = useState<Map<string, string>>(new Map());
  // filledBy: backend'in filled_by dict'ini yansıtır — ilk dolduranı kaydeder
  const [filledBy, setFilledBy] = useState<Map<string, string>>(new Map());
  // Tüm oyuncuların board'ları (adaylar hesaplama için)
  const [allBoards, setAllBoards] = useState<Record<string, (number | null)[][]>>({});
  // Çevrimdışı oyuncular — bağlantısı kopmuş ama kaydı korunuyor
  const [offlinePlayers, setOfflinePlayers] = useState<Set<string>>(new Set());

  // Ranked countdown overlay
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const MAX_MISTAKES = 3;
  const MAX_HINTS = 3;

  // Elendi/bitti uyarı toast
  const prevIsFailedRef = useRef(false);
  const prevIsFinishedRef = useRef(false);
  useEffect(() => {
    const nowFailed = !!(myState?.is_failed || gb.mistakes >= MAX_MISTAKES);
    const nowFinished = !!myState?.is_finished;
    if (nowFailed && !prevIsFailedRef.current) {
      setEliminatedToast(true);
      setTimeout(() => setEliminatedToast(false), 3500);
    }
    if (nowFinished && !prevIsFinishedRef.current) {
      setFinishedToast(true);
      fx.triggerComplete(4, 4);
      setTimeout(() => setFinishedToast(false), 3500);
    }
    prevIsFailedRef.current = nowFailed;
    prevIsFinishedRef.current = nowFinished;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myState?.is_failed, myState?.is_finished, gb.mistakes]);

  // statusRef — socket closure'larında güncel değeri okumak için
  statusRef.current = status;
  userRef.current = user;

  // Waiting aşamasında 1sn'de bir oda durumunu sorgula
  useEffect(() => {
    if (status !== 'waiting' || !socket) return;
    pollRef.current = setInterval(() => {
      socket.emit('get_room_state');
    }, 1000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [status, socket]);

  /** Puzzle verisinden board state'ini kur.
   *  myBoardData: bu oyuncunun backend'deki board durumu (reconnect için).
   *  filledByData: hangi hücreyi kimin doldurduğu (rakip hint için).
   */
  const setupBoard = useCallback((
    puz: (number | null)[][],
    sol: (number | null)[][] = [],
    filledByData?: Record<string, string>,
    myBoardData?: (number | null)[][],
  ) => {
    setSolution(sol);

    // Başlangıç: yalnızca puzzle'ın verili hücreleri
    const newBoard = puz.map(row => [...row]);
    const newRivalHints = new Map<string, string>();

    // Kendi doldurduğum hücreleri geri yükle (sayfa yenilemesi / geç katılım)
    if (myBoardData) {
      for (let r = 0; r < 9; r++) {
        for (let c = 0; c < 9; c++) {
          const myVal = myBoardData[r][c];
          const given = puz[r][c];
          if (myVal != null && myVal !== 0 && (given == null || given === 0)) {
            newBoard[r][c] = myVal;
          }
        }
      }
    }

    // Rakiplerin doldurduğu hücreleri hint olarak işaretle (ben doldurmadıysam)
    const newFilledBy = new Map<string, string>();
    if (filledByData) {
      for (const [key, pid] of Object.entries(filledByData)) {
        newFilledBy.set(key, pid);
        const [r, c] = key.split('-').map(Number);
        if (newBoard[r][c] == null || newBoard[r][c] === 0) {
          newRivalHints.set(key, pid);
        }
      }
    }

    gb.resetBoard(puz, newBoard);
    setRivalHints(newRivalHints);
    setFilledBy(newFilledBy);
    setAllBoards({});
  }, [gb.resetBoard]);

  // Socket setup
  useEffect(() => {
    if (!nicknameConfirmed) return;
    if (!token) return;

    const s = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token },
    });
    setSocket(s);

    s.on('connect', () => {
      setSocketConnected(true);
      if (roomId) {
        s.emit('join_room', {
          room_id: roomId,
          nickname: nicknameRef.current,
          player_id: playerId,
          user_id: userRef.current?.id || '',
        });
      }
    });

    s.on('disconnect', () => {
      setSocketConnected(false);
    });

    s.on('room_joined', (data: any) => {
      setRoomCode(data.room_code || '');
      setRoomName(data.room_name || '');
      if (data.room_type) setRoomType(data.room_type);
      setHostId(data.host_id || '');
      setDifficulty(data.difficulty || 3);
      setStatus(data.status || 'waiting');
      if (data.allow_hints !== undefined) setAllowHints(data.allow_hints);
      if (data.allow_candidates !== undefined) setAllowCandidates(data.allow_candidates);
      if (data.players) setPlayers(data.players);
      // Aktif oda bilgisini sakla — Lobby'e dönünce rejoin teklif edilsin
      localStorage.setItem('sudoku_active_room', JSON.stringify({
        room_id: roomId,
        room_code: data.room_code || '',
        room_name: data.room_name || '',
      }));
      if (data.status === 'playing' && data.puzzle) {
        // Reconnect: board'u kur, ardından backend'den gelen değerleri geri yükle
        setupBoard(data.puzzle, data.solution || [], data.filled_by, data.my_board);
        if (data.my_notes) gb.setNotes(data.my_notes);  // reconnect'te notlar kaybolmasın
        // Kendi ilerlememizi geri yükle (mistakes, hintsUsed sıfırlanmasın)
        const myP = data.players?.[playerId];
        if (data.my_mistakes != null) gb.setMistakes(data.my_mistakes);
        else if (myP) gb.setMistakes(myP.mistakes ?? 0);
        if (data.my_hints_used != null) gb.setHintsUsed(data.my_hints_used);
        else if (myP) gb.setHintsUsed(myP.hints_used ?? 0);
        if (data.my_elapsed != null) {
          gb.setElapsed(Math.floor(data.my_elapsed));
        }
        if (data.all_boards) {
          setAllBoards(data.all_boards);
        }
      }
    });

    s.on('player_joined', (data: any) => {
      if (data.players) setPlayers(data.players);
      if (data.room_name != null) setRoomName(data.room_name);
      if (data.player_id) setOfflinePlayers(prev => { const s = new Set(prev); s.delete(data.player_id); return s; });
    });

    s.on('player_left', (data: any) => {
      if (data.players) setPlayers(data.players);
      if (data.player_id) setOfflinePlayers(prev => new Set(prev).add(data.player_id));
    });

    // Periyodik polling'den gelen güncel oda durumu
    s.on('room_state', (data: any) => {
      if (data.players) setPlayers(data.players);
      if (data.host_id) setHostId(data.host_id);
      // game_started kaçırıldıysa polling ile yakala
      if (data.status === 'playing' && data.puzzle && statusRef.current !== 'playing') {
        setStartingGame(false);
        setStatus('playing');
        setupBoard(data.puzzle, data.solution || [], data.filled_by, data.my_board);
        if (data.my_notes) gb.setNotes(data.my_notes);  // reconnect'te notlar kaybolmasın
        // Kendi ilerlememizi geri yükle
        const myP = data.players?.[playerId];
        if (myP) {
          gb.setMistakes(myP.mistakes ?? 0);
          gb.setHintsUsed(myP.hints_used ?? 0);
          if (myP.solver_used) setUsedSolver(true);
        }
        if (data.my_elapsed != null) {
          gb.setElapsed(Math.floor(data.my_elapsed));
        }
      }
    });

    s.on('countdown', (data: { seconds: number }) => {
      setCountdownSeconds(data.seconds);
    });

    s.on('game_started', (data: any) => {
      setCountdownSeconds(null);
      setStartingGame(false);
      setRestartDifficulty(null);
      if (!data.puzzle) {
        console.error('game_started: puzzle eksik', data);
        return;
      }
      setLeaderboard([]);
      setStatus('playing');
      // Yeni oyun başladı: solver kilidi sıfırla
      sessionStorage.removeItem(`solver_used_${roomId}`);
      setUsedSolver(false);
      // Oyuncu durumlarını sıfırla (is_finished: true kalmaması için — rematch fix)
      if (data.players) setPlayers(data.players);
      setupBoard(data.puzzle, data.solution || []);
    });

    s.on('player_progress', (data: any) => {
      if (data.players) setPlayers(data.players);
      // Tüm oyuncuların board'larını güncelle
      if (data.all_boards) {
        setAllBoards(data.all_boards);
      }
      if (data.cell_update) {
        const { player_id: pid, row, col } = data.cell_update;
        const key = `${row}-${col}`;

        // filledBy: ilk dolduranı kaydet (backend ile tutarlı)
        setFilledBy(prev => {
          if (prev.has(key)) return prev;
          const next = new Map(prev);
          next.set(key, pid);
          return next;
        });

        if (pid !== playerId) {
          // Rakip doğru hamle yaptı → hint olarak işaretle (değer gösterme)
          setRivalHints(prev => {
            const next = new Map(prev);
            next.set(key, pid);
            return next;
          });
        }
        // Kendi hamlem: zaten placeValue içinde board güncellendi, burada tekrar güncellemeye gerek yok
      }
    });

    s.on('player_finished', (data: any) => {
      if (data.players) setPlayers(data.players);
      if (data.leaderboard) setLeaderboard(data.leaderboard);
    });

    s.on('game_over', (data: any) => {
      setStatus('finished');
      if (data.leaderboard) setLeaderboard(data.leaderboard);
      if (data.players) setPlayers(data.players);
    });

    s.on('solver_used', (data: { player_id: string }) => {
      setPlayers(prev => {
        const p = prev[data.player_id];
        if (!p) return prev;
        return { ...prev, [data.player_id]: { ...p, solver_used: true } };
      });
    });

    s.on('error', (data: any) => {
      setStartingGame(false);
      showAlert(data.message);
    });

    s.on('match_cancelled', (data: any) => {
      localStorage.removeItem('sudoku_active_room');
      showAlert(data.message || t.room.matchCancelled);
      navigate('/');
    });

    s.on('achievement_unlocked', (data: { ids: string[] }) => {
      if (data.ids?.length) setNewAchievements(prev => [...prev, ...data.ids]);
    });

    s.on('rating_update', (data: { delta: number; old_rating: number; new_rating: number }) => {
      setRatingUpdate(data);
    });

    s.on('xp_update', (data: { delta: number; total_xp: number; old_level: number; new_level: number; leveled_up: boolean; xp_into_level: number; xp_for_next: number }) => {
      setXpInfo(data);
    });

    return () => { s.disconnect(); };
  }, [roomId, playerId, nicknameConfirmed, setupBoard, token]);

  const confirmNickname = () => {
    const name = nickname.trim();
    if (!name) {
      showAlert(t.room.nicknameRequired);
      return;
    }
    localStorage.setItem('sudoku_nickname', name);
    setNickname(name);
    setNicknameConfirmed(true);
  };

  // Hücreye değer yaz — bağımsız tahta, rakip hint'i temizle
  const placeValue = useCallback((_r: number, _c: number, value: number) => {
    if (!gb.selectedCell || status !== 'playing') return;
    const [row, col] = gb.selectedCell;
    if (isGiven(gb.puzzle, row, col)) return;
    if (myState?.is_finished || myState?.is_failed) return;
    if (usedSolver) return;
    if (gb.mistakes >= MAX_MISTAKES) return;
    if (gb.board[row]?.[col] != null && gb.board[row][col] !== 0) return;

    if (gb.noteMode) {
      if (gb.showCandidates) return; // Adaylar açıkken not editleme kapalı (Approach A)
      gb.setNotes(prev => toggleNoteLocal(prev, row, col, value));
      socket?.emit('toggle_note', { room_id: roomId, player_id: playerId, row, col, value });
      return;
    }

    const correct = solution.length ? solution[row]?.[col] === value : undefined;
    if (correct === true) {
      gb.setBoard(prev => {
        const b = prev.map(r => [...r]);
        b[row][col] = value;
        return b;
      });
      const updatedBoard = gb.board.map((r, ri) => r.map((cell, ci) => ri === row && ci === col ? value : cell));
      fx.playCorrect();
      fx.vibrateCorrect();
      fx.triggerCorrect(row, col, updatedBoard, value);
      setCombo(c => c + 1);
      gb.setNotes(prev => {
        if (gb.autoCleanNotes) return clearAutoNotes(prev, row, col, value);
        const n = prev.map(r => r.map(c => [...c]));
        n[row][col] = [];
        return n;
      });
      setRivalHints(prev => {
        if (!prev.has(`${row}-${col}`)) return prev;
        const next = new Map(prev);
        next.delete(`${row}-${col}`);
        return next;
      });
    } else if (correct === false) {
      gb.setMistakes(m => m + 1);
      setCombo(0);
      gb.setWrongCell([row, col]);
      setTimeout(() => gb.setWrongCell(null), 1000);
      fx.playWrong();
      fx.vibrateWrong();
      fx.triggerWrong(row, col);
    } else {
      socket?.once('place_result', (data: any) => {
        if (data.error) {
          // Oda backend restart/deploy sonrası bellekte kalmamış — asla "yanlış cevap" sayma
          showAlert(t.room.roomLost);
          navigate('/lobby', { replace: true });
          return;
        }
        if (data.correct) {
          const updatedBoard = gb.board.map((r, ri) =>
            r.map((cell, ci) => ri === row && ci === col ? value : cell)
          );
          gb.setBoard(updatedBoard);
          gb.setNotes(prev => {
            if (gb.autoCleanNotes) return clearAutoNotes(prev, row, col, value);
            const next = prev.map(r => r.map(c => [...c]));
            next[row][col] = [];
            return next;
          });
          fx.playCorrect();
          fx.vibrateCorrect();
          fx.triggerCorrect(row, col, updatedBoard, value);
        } else {
          gb.setMistakes(data.mistakes ?? (gb.mistakes + 1));
          gb.setWrongCell([row, col]);
          setTimeout(() => gb.setWrongCell(null), 1000);
          fx.playWrong();
          fx.vibrateWrong();
          fx.triggerWrong(row, col);
        }
      });
    }

    socket?.emit('place_cell', { room_id: roomId, player_id: playerId, row, col, value, user_id: userRef.current?.id || '' });
  }, [gb.selectedCell, status, gb.puzzle, solution, gb.noteMode, myState, socket, roomId, playerId, gb.board, usedSolver, gb.mistakes, gb.autoCleanNotes, nickname, players]);
  placeValueRef.current = placeValue;

  // Silme
  const eraseCell = useCallback((_r: number, _c: number) => {
    if (!gb.selectedCell || status !== 'playing') return;
    const [r, c] = gb.selectedCell;
    if (isGiven(gb.puzzle, r, c)) return;

    const key = `${r}-${c}`;
    const originalFiller = filledBy.get(key);
    if (originalFiller && originalFiller !== playerId) {
      setRivalHints(prev => {
        const next = new Map(prev);
        next.set(key, originalFiller);
        return next;
      });
    }

    gb.setBoard(prev => {
      const b = prev.map(row => [...row]);
      b[r][c] = null;
      return b;
    });
    gb.setNotes(prev => {
      const n = prev.map(row => row.map(col => [...col]));
      n[r][c] = [];
      return n;
    });
  }, [gb.selectedCell, status, gb.puzzle, filledBy, playerId]);
  eraseRef.current = eraseCell;

  // İpucu — solution'dan rastgele boş hücre ver (client-side)

  const getHint = useCallback(() => {
    if (status !== 'playing' || myState?.is_finished || myState?.is_failed) return;
    if (gb.hintsUsed >= MAX_HINTS) return;
    if (!solution.length) return;
    const emptyCells: [number, number][] = [];
    for (let r = 0; r < 9; r++)
      for (let c = 0; c < 9; c++)
        if ((gb.board[r][c] == null || gb.board[r][c] === 0) && solution[r]?.[c])
          emptyCells.push([r, c]);
    if (emptyCells.length === 0) return;
    const [hr, hc] = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const val = solution[hr][hc]!;
    gb.setBoard(prev => {
      const b = prev.map(row => [...row]);
      b[hr][hc] = val;
      return b;
    });
    gb.setNotes(prev => clearAutoNotes(prev, hr, hc, val));
    setRivalHints(prev => {
      if (!prev.has(`${hr}-${hc}`)) return prev;
      const next = new Map(prev);
      next.delete(`${hr}-${hc}`);
      return next;
    });
    gb.setHintsUsed(h => h + 1);
    gb.setSelectedCell([hr, hc]);
    socket?.emit('place_cell', { room_id: roomId, player_id: playerId, row: hr, col: hc, value: val, is_hint: true, user_id: userRef.current?.id || '' });
  }, [status, myState, gb.board, solution, playerId, socket, roomId, gb.hintsUsed]);

  // "Tek Adımı Açıkla": mevcut tahtadan bulunan ilk mantıksal adımı teknik+neden
  // ile gösterir. Mevcut ipucu ile aynı bilgi sınıfı (tek hücre) — solver_used
  // kilidini TETİKLEMEZ, oyuna devam edilebilir.
  const explainStep = useCallback(() => {
    if (status !== 'playing' || myState?.is_finished || myState?.is_failed) return;
    if (gb.hintsUsed >= MAX_HINTS || !allowHints) return;
    socket?.emit('room_explain_hint', { room_id: roomId, player_id: playerId });
    socket?.once('room_explain_hint_result', (data: any) => {
      if (data.error) {
        showAlert(data.error === 'hint_limit' ? t.game.hintLimit : data.error, t.game.hintTitle);
        return;
      }
      if (data.applied) {
        gb.setBoard(prev => { const b = prev.map((row: any) => [...row]); b[data.row][data.col] = data.value; return b; });
        gb.setNotes(prev => clearAutoNotes(prev, data.row, data.col, data.value));
        gb.setSelectedCell([data.row, data.col]);
        setRivalHints(prev => {
          const key = `${data.row}-${data.col}`;
          if (!prev.has(key)) return prev;
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        setFilledBy(prev => { const m = new Map(prev); m.set(`${data.row}-${data.col}`, playerId); return m; });
      }
      gb.setHintsUsed(data.hints_used);

      const slug = techniqueSlug(data.technique);
      const level = techniqueLevel(data.technique);
      const localizedName = slug ? (t.edu.techniques as any)[slug]?.name : undefined;
      showAlert(explainStepText(data, locale), `${localizedName ?? data.technique} · ${t.difficulties[level]}`);
    });
  }, [status, myState, gb, allowHints, socket, roomId, playerId, showAlert, t, locale]);

  // Tüm adayları göster/gizle
  // Adaylar modu (Approach A): canlı overlay, manuel notları ezmez. Açınca not
  // modu kapanır (karşılıklı dışlayan).
  const toggleCandidates = useCallback(() => {
    const next = !gb.showCandidates;
    gb.setShowCandidates(next);
    if (next) gb.setNoteMode(false);
  }, [gb.showCandidates, gb.setShowCandidates, gb.setNoteMode]);

  // Multiplayer merged candidates — benim + rakip doldurduğu hücreleri hesaba
  // katan canlı adaylar. showCandidates açıkken hesaplanır, GameGrid'e overlay
  // olarak geçilir (notlara yazılmaz).
  const mpCandidates = useMemo<number[][][] | null>(() => {
    if (!gb.showCandidates) return null;
    const mergedBoard: number[][] = Array(9).fill(null).map(() => Array(9).fill(0));
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (gb.board[r][c] != null && gb.board[r][c] !== 0) {
          mergedBoard[r][c] = gb.board[r][c] as number;
        } else {
          for (const rivalBoard of Object.values(allBoards)) {
            if (rivalBoard[r][c] != null && rivalBoard[r][c] !== 0) {
              mergedBoard[r][c] = rivalBoard[r][c] as number;
              break;
            }
          }
        }
      }
    }
    const cands: number[][][] = Array.from({ length: 9 }, () => Array.from({ length: 9 }, () => []));
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (gb.board[r][c] != null && gb.board[r][c] !== 0) continue;
        const used = new Set<number>();
        for (let i = 0; i < 9; i++) if (i !== c && mergedBoard[r][i]) used.add(mergedBoard[r][i]);
        for (let i = 0; i < 9; i++) if (i !== r && mergedBoard[i][c]) used.add(mergedBoard[i][c]);
        const br = Math.floor(r / 3) * 3, bc = Math.floor(c / 3) * 3;
        for (let rr = br; rr < br + 3; rr++)
          for (let cc = bc; cc < bc + 3; cc++)
            if ((rr !== r || cc !== c) && mergedBoard[rr][cc]) used.add(mergedBoard[rr][cc]);
        for (let n = 1; n <= 9; n++) if (!used.has(n)) cands[r][c].push(n);
      }
    }
    return cands;
  }, [gb.showCandidates, gb.board, allBoards]);

  // Hücre arka plan rengi — multiplayer renk sistemi
  const getCellStyle = (r: number, c: number): React.CSSProperties | undefined => {
    if (!showColors) return undefined;
    if (gb.selectedCell && gb.selectedCell[0] === r && gb.selectedCell[1] === c) return undefined;
    if (gb.highlightEnabled && gb.highlightDigit && gb.board[r]?.[c] === gb.highlightDigit) return undefined;
    const key = `${r}-${c}`;
    if (!isGiven(gb.puzzle, r, c) && gb.board[r]?.[c] != null && gb.board[r][c] !== 0) {
      const color = players[playerId]?.color;
      if (color) return { backgroundColor: `${color}20` };
    }
    const rivalPid = rivalHints.get(key);
    if (rivalPid) {
      const isOffline = offlinePlayers.has(rivalPid);
      const color = players[rivalPid]?.color;
      if (isOffline) return { backgroundColor: 'var(--bg-cell-shaded)' };
      if (color) return { backgroundColor: `${color}35` };
    }
    return undefined;
  };

  const getCellValueStyle = (r: number, c: number): React.CSSProperties | undefined => {
    if (!showColors) return undefined;
    if (isGiven(gb.puzzle, r, c)) return undefined;
    const color = players[playerId]?.color;
    if (!color) return undefined;
    return { color };
  };

  // Rakip dot render — GameGrid'in renderCellExtra'sı
  const renderCellExtra = (r: number, c: number) => {
    if (!showRivalDots || !rivalHints.has(`${r}-${c}`)) return null;
    const rpid = rivalHints.get(`${r}-${c}`)!;
    const dotColor = showColors ? (players[rpid]?.color || 'var(--text-secondary)') : 'var(--text-secondary)';
    // Notlar varsa overlay, yoksa normal dot
    if (gb.notes[r]?.[c]?.length > 0) {
      return <span className="rival-hint-dot rival-hint-dot-overlay" style={{ color: dotColor }}>&#9679;</span>;
    }
    if (gb.board[r]?.[c] == null || gb.board[r][c] === 0) {
      return <span className="rival-hint-dot" style={{ color: dotColor }}>&#9679;</span>;
    }
    return null;
  };

  const startGame = () => {
    if (!socketConnected) {
      showAlert('Sunucuya bağlantı bekleniyor...');
      return;
    }
    setStartingGame(true);
    socket?.emit('start_game', { room_id: roomId, player_id: playerId });
    setTimeout(() => setStartingGame(false), 15000);
  };

  // Lobby'ye dön — odayı kapatmaz, arka planda açık kalır ve
  // RoomNotifier arkadaş katıldığında/oda dolduğunda bildirir.
  const leaveRoom = () => {
    navigate('/lobby');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  // Tek sıralama fonksiyonu — chip bar ve rekabet tablosu tutarlı
  const sortPlayers = (a: [string, PlayerInfo], b: [string, PlayerInfo]) => {
    const scoreDiff = (b[1].score ?? 0) - (a[1].score ?? 0);
    if (scoreDiff !== 0) return scoreDiff;
    const progressDiff = (b[1].progress ?? 0) - (a[1].progress ?? 0);
    if (progressDiff !== 0) return progressDiff;
    const mistakesDiff = (a[1].mistakes ?? 0) - (b[1].mistakes ?? 0);
    if (mistakesDiff !== 0) return mistakesDiff;
    return (a[1].elapsed ?? 0) - (b[1].elapsed ?? 0);
  };

  const playerList = Object.entries(players).sort(sortPlayers);
  const standings = Object.entries(players).sort(sortPlayers);

  const handleNumpadClick = (n: number) => {
    gb.setHighlightDigit(n);
    placeValue(-1, -1, n);
  };

  // ─── NICKNAME PROMPT ─────────────────────
  if (!nicknameConfirmed) {
    return (
      <div className="room-page">
        {modalElement}
        <AppHeader onBack={() => navigate('/')} backLabel={t.common.home} title={t.room.waitingRoom} />
        <div className="nickname-prompt">
          <p>{t.room.enterNickname}</p>
          <input
            type="text"
            className="nickname-input"
            value={nickname}
            onChange={e => setNickname(e.target.value)}
            placeholder={t.room.nicknamePlaceholder}
            maxLength={20}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') confirmNickname(); }}
          />
          <button className="start-btn" onClick={confirmNickname}>
            {t.room.joinRoom}
          </button>
        </div>
      </div>
    );
  }

  // ─── WAITING ────────────────────────────────
  if (status === 'waiting') {
    return (
      <div className="room-page">
        {modalElement}
        <AppHeader onBack={leaveRoom} backLabel={t.room.backToLobby} title={roomName || t.room.waitingRoom} />

        <div className="room-code-section">
          <span className="code-label">{t.room.roomCode}</span>
          <span className="room-code-display">{roomCode}</span>
          <button className="copy-btn" onClick={copyCode}>{codeCopied ? t.room.copied : t.common.copy}</button>
          <button className="copy-btn" onClick={copyLink}>{t.room.shareLink}</button>
        </div>

        <div className="room-meta">
          <span className="meta-badge">{t.room.difficultyBadge}: {t.difficulties[difficulty]}</span>
          <span className="meta-badge">{playerList.length} {t.room.playerCountBadge}</span>
        </div>

        <div className="player-list">
          <h3>{t.room.players}</h3>
          {playerList.map(([pid, p]) => (
            <div key={pid} className={`player-item ${pid === hostId ? 'host' : ''}`}>
              <span>
                <span className="player-color-dot" style={{ background: p.color }}></span>
                {p.nickname}
              </span>
              {pid === hostId && <span className="host-tag">{t.room.hostTag}</span>}
            </div>
          ))}
        </div>

        {!socketConnected && (
          <div className="waiting-msg">{t.lobby.connectingBadge}</div>
        )}
        {isHost && playerList.length >= 1 && (
          <button
            className="start-btn"
            onClick={startGame}
            disabled={startingGame || !socketConnected}
          >
            {startingGame ? t.room.startingGame : t.room.startGame}
          </button>
        )}
        {!isHost && (
          <div className="waiting-msg">{t.room.waitingForHost}</div>
        )}

        {/* Ranked geri sayım overlay */}
        {countdownSeconds !== null && (
          <div className="countdown-overlay">
            <div className="countdown-number" key={countdownSeconds}>{countdownSeconds}</div>
            <p className="countdown-label">
              {t.challenge.startingIn.replace('{n}', String(countdownSeconds))}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ─── FINISHED ────────────────────────────────
  if (status === 'finished') {
    return (
      <div className="game-page">
        {modalElement}
        <AchievementToast achievementIds={newAchievements} onDone={() => setNewAchievements([])} />
        <RatingDeltaToast
          delta={ratingUpdate?.delta ?? null}
          oldRating={ratingUpdate?.old_rating}
          newRating={ratingUpdate?.new_rating}
          onDone={() => setRatingUpdate(null)}
        />
        <AppHeader onBack={() => { localStorage.removeItem('sudoku_active_room'); navigate('/lobby', { replace: true }); }} backLabel={t.room.backToLobby} title={t.room.gameOver} />
        <div className="mp-finish-screen">
          {xpInfo && (
            <div className="finish-xp-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, margin: '6px 0 14px' }}>
              <LevelBadge level={xpInfo.new_level} size="lg" />
              <div style={{ fontWeight: 800, color: 'var(--warning-text)', fontSize: 18 }}>
                +{xpInfo.delta} XP
              </div>
              {xpInfo.leveled_up && (
                <div style={{ fontWeight: 900, color: 'var(--warning-text)', fontSize: 16, padding: '4px 10px', borderRadius: 999, background: 'var(--warning-bg)', border: '1px solid var(--warning-border)' }}>
                  {t.profile.levelUp}
                </div>
              )}
            </div>
          )}
          {/* Sıralama — tören reveal: sonuncudan birinciye */}
          <div className="mp-lb">
            <h2 className="mp-lb-title">{t.room.leaderboard}</h2>
            {[...standings].reverse().map(([pid, p], revIdx) => {
              const idx = standings.length - 1 - revIdx;
              const medalClass = idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? 'bronze' : '';
              const medalIcon = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
              const isMe = pid === playerId;
              const mistakePenalty = (p.mistakes ?? 0) * 5;
              const hintPenalty = (p.hints_used ?? 0) * 20;
              const bonus = p.finish_bonus ?? 0;
              return (
                <div
                  key={pid}
                  className={`mp-lb-row ${medalClass} ${isMe ? 'is-me' : ''} ${idx === 0 ? 'first-place' : ''}`}
                  style={{ animationDelay: `${revIdx * 180}ms` }}
                >
                  <div className="mp-lb-main">
                    <span className="mp-lb-rank">
                      {medalIcon ?? `#${idx + 1}`}
                    </span>
                    <span className="mp-lb-dot" style={{ background: p.color }} />
                    <span className="mp-lb-name" style={{ color: p.color }}>{p.nickname}</span>
                    <span className="mp-lb-score">{p.score ?? 0}<span className="mp-lb-score-unit">p</span></span>
                    <span className="mp-lb-time">{formatTime(p.elapsed ?? 0)}</span>
                  </div>
                  {/* Kişisel breakdown — herkes için göster */}
                  <div className="mp-lb-breakdown">
                    {mistakePenalty > 0 && <span className="mp-bd-item mp-bd-neg">{p.mistakes} {t.game.errorCount.toLowerCase()} −{mistakePenalty}p</span>}
                    {hintPenalty > 0 && <span className="mp-bd-item mp-bd-neg">{p.hints_used} {t.game.hintCount.toLowerCase()} −{hintPenalty}p</span>}
                    {bonus > 0 && <span className="mp-bd-item mp-bd-pos">{t.room.finishBonus} +{bonus}p</span>}
                    {mistakePenalty === 0 && hintPenalty === 0 && <span className="mp-bd-item mp-bd-pristine">✦ {t.room.pristine}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Kendi istatistiklerim */}
          <div className="mp-my-stats">
            <div className="mp-stat"><span>{t.game.duration}</span><strong>{formatTime(gb.elapsed)}</strong></div>
            <div className="mp-stat"><span>{t.game.errorCount}</span><strong>{myState?.mistakes ?? gb.mistakes}</strong></div>
            <div className="mp-stat"><span>{t.game.hintCount}</span><strong>{gb.hintsUsed}</strong></div>
            <div className="mp-stat">
              <span>{t.common.difficulty}</span>
              <strong style={{ color: DIFF_COLORS[difficulty] }}>{t.difficulties[difficulty]}</strong>
            </div>
          </div>

          <div style={{ marginTop: 16 }}><AdBanner slot={AD_SLOTS.gameEnd} /></div>

          {/* Misafir → zaferi/puanı kaydet daveti */}
          {isGuestUser && (
            <div className="guest-invite-banner guest-invite-win">
              <span className="guest-invite-text">{t.home.guestWinInviteText}</span>
              <button
                type="button"
                className="guest-invite-cta"
                onClick={() => { localStorage.removeItem('sudoku_active_room'); navigate('/auth', { state: { from: '/lobby' } }); }}
              >
                {t.home.guestLockRegisterNow}
              </button>
            </div>
          )}

          {/* Aksiyonlar */}
          <div className="mp-finish-actions">
            {isHost && (
              <div className="mp-restart-area">
                <div className="mp-restart-diff-row">
                  <span className="mp-restart-diff-label">{t.room.chooseDifficultyLabel}</span>
                  {[1, 2, 3, 4, 5].map(d => (
                    <button
                      key={d}
                      className={`mp-restart-diff-btn ${(restartDifficulty ?? difficulty) === d ? 'selected' : ''}`}
                      style={{ color: DIFF_COLORS[d] }}
                      onClick={() => setRestartDifficulty(d)}
                    >
                      {t.difficulties[d]}
                    </button>
                  ))}
                </div>
                <button className="mp-btn-primary" onClick={() => {
                  socket?.emit('restart_game', { room_id: roomId, player_id: playerId, difficulty: restartDifficulty ?? difficulty });
                  setStartingGame(true);
                  setTimeout(() => setStartingGame(false), 15000);
                }} disabled={startingGame}>
                  {startingGame ? t.room.startingGame : t.room.playAgain}
                </button>
              </div>
            )}
            {user && Object.entries(players)
              .filter(([pid, p]) => pid !== playerId && !friendNicks.has(p.nickname))
              .slice(0, 1).map(([pid, p]) => (
              <button
                key={`addfriend-${pid}`}
                className="mp-btn-secondary"
                disabled={friendRequestState === 'sending' || friendRequestState === 'sent'}
                onClick={async () => {
                  if (!token) return;
                  setFriendRequestState('sending');
                  try {
                    const res = await fetch(`${SOCKET_URL}/friends/request`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ nickname: p.nickname }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok) {
                      setFriendRequestState('sent');
                      setFriendRequestMsg(data.message || t.friends.requestSent);
                    } else {
                      setFriendRequestState('error');
                      setFriendRequestMsg(data.detail || t.friends.requestFailed);
                    }
                  } catch {
                    setFriendRequestState('error');
                    setFriendRequestMsg(t.friends.networkError);
                  }
                }}
              >
                {friendRequestState === 'sent'
                  ? `✓ ${friendRequestMsg || t.friends.requestSent}`
                  : friendRequestState === 'sending'
                  ? t.common.loading
                  : friendRequestState === 'error'
                  ? friendRequestMsg
                  : `${p.nickname} · ${t.friends.sendRequestBtn}`}
              </button>
            ))}
            <button className="mp-btn-secondary" onClick={() => { localStorage.removeItem('sudoku_active_room'); navigate('/lobby', { replace: true }); }}>{t.room.goToLobby}</button>
            <button className="mp-btn-secondary" onClick={() => navigate('/')}>{t.common.home}</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── PLAYING ── GamePage ile aynı layout ──────
  return (
    <div className="game-page">
      {modalElement}
      <SEO path="/room" />
      {codeCopied && <div className="copy-toast">{t.room.codeCopied}</div>}
      {eliminatedToast && <div className="copy-toast mp-eliminated-toast">❌ {t.room.eliminated} {t.game.tooManyMistakes}</div>}
      {finishedToast && <div className="copy-toast mp-finished-toast">🎉 {t.game.congratulations}</div>}
      <AppHeader
        onBack={leaveRoom}
        backLabel={t.room.backToLobby}
        showAuth={false}
        extraControls={<>
          <FontSizeToggle />
          <SettingsPopup
            isOpen={gb.showSettings}
            onToggle={() => gb.setShowSettings(v => !v)}
            buttonLabel={t.room.settingsBtn}
            settings={[
              { key: 'colors', labelOn: t.room.hideColors, labelOff: t.room.showColors, value: showColors, onToggle: () => setShowColors(v => !v), icon: '\u25D1' },
              { key: 'dots', labelOn: t.room.hideDots, labelOff: t.room.showDots, value: showRivalDots, onToggle: () => setShowRivalDots(v => !v), icon: '\u25CF' },
              { key: 'autoClean', labelOn: t.game.autoCleanOn, labelOff: t.game.autoCleanOff, value: gb.autoCleanNotes, onToggle: () => gb.setAutoCleanNotes(v => !v) },
              { key: 'highlight', labelOn: t.game.highlightOn, labelOff: t.game.highlightOff, value: gb.highlightEnabled, onToggle: () => gb.setHighlightEnabled(v => !v) },
              { key: 'cellHighlight', labelOn: t.game.cellHighlightOn, labelOff: t.game.cellHighlightOff, value: gb.cellHighlightEnabled, onToggle: () => gb.setCellHighlightEnabled(v => !v) },
              { key: 'effects', labelOn: t.game.effectsOn, labelOff: t.game.effectsOff, value: fx.isEffectsEnabled, onToggle: fx.toggleEffects },
              { key: 'sound', labelOn: t.game.soundOn, labelOff: t.game.soundOff, value: fx.isSoundEnabled, onToggle: fx.toggleSound },
            ] as SettingToggle[]}
          />
        </>}
      />

      <div className="game-layout mp-layout">
        <div className="mp-top-row">
        {/* Mini scoreboard — oyun sırasında tüm oyuncuların anlık skoru */}
        <div className="room-mini-lb">
          {playerList.map(([pid, p]) => (
            <div key={pid} className={`room-mini-player${pid === playerId ? ' room-mini-player-me' : ''}`}>
              <span className="room-mini-dot" style={{ background: p.color }} />
              <span className="room-mini-name">{p.nickname}</span>
              <span className="room-mini-score">{p.score ?? 0}p</span>
            </div>
          ))}
        </div>
        <div className="mp-panels-row">
        <div className="mp-board-area">
          {fx.floatingScores.map(s => (
            <div
              key={s.id}
              className="float-score"
              style={{ '--float-offset': `${s.offset}px`, '--float-row': String(s.row), '--float-col': String(s.col) } as React.CSSProperties}
            >{s.text}</div>
          ))}
          <Confetti active={fx.showConfetti} />
          <GameStatusBar
            className="mp-status-bar"
            elapsed={gb.elapsed}
            score={myState?.score ?? gb.score}
            scorePulsing={fx.scorePulsing}
            mistakes={myState?.mistakes ?? gb.mistakes}
            maxMistakes={MAX_MISTAKES}
            difficultyLabel={t.difficulties[difficulty]}
            difficultyColor={DIFF_COLORS[difficulty]}
            isPaused={gb.isPaused}
            onTogglePause={() => gb.setIsPaused(p => !p)}
            centerExtra={
              <>
                {(myState?.is_failed || (myState?.mistakes ?? gb.mistakes) >= MAX_MISTAKES) && (
                  <span className="fail-badge">{t.room.failedLabel}</span>
                )}
                <ComboCounter count={combo} onExpire={() => setCombo(0)} />
              </>
            }
            roomShareSlot={!isRankedRoom && (roomName || roomCode) ? (
              <button className="room-header-chip" onClick={copyLink} title={t.room.shareLink}>
                {roomName && <>{t.room.roomLabel}: {roomName}</>}
                {roomName && roomCode && <span className="room-header-sep">·</span>}
                {roomCode && <>{t.room.code}: {roomCode}</>}
                <span className="room-header-sep">·</span>
                <span>{t.room.shareLink}</span>
              </button>
            ) : undefined}
            opponentRatingSlot={(isRankedRoom || roomType === 'challenge') && opponent ? (
              <span className="diff-badge">vs {opponent.nickname}{opponent.rating ? ` · ${opponent.rating}` : ''}</span>
            ) : undefined}
          />

        <GameGrid
          board={gb.board}
          notes={gb.notes}
          showCandidates={gb.showCandidates}
          candidatesOverride={mpCandidates}
          isPaused={gb.isPaused}
          getCellClass={(r, c) => { const base = gb.getCellClass(r, c); const e = fx.getCellEffectClass(r, c); return e ? `${base} ${e}` : base; }}
          onCellClick={gb.handleCellClick}
          onResumeFromPause={() => gb.setIsPaused(false)}
          getCellStyle={(r, c) => { const ex = getCellStyle(r, c); const ef = fx.getCellEffectStyle(r, c); return (ex || ef) ? { ...ex, ...ef } : undefined; }}
          getCellValueStyle={getCellValueStyle}
          renderCellExtra={renderCellExtra}
        />

        <Numpad
          board={gb.board}
          highlightDigit={gb.highlightDigit}
          onNumberClick={handleNumpadClick}
          className="mp-numpad-mobile"
        />
        </div>{/* /mp-board-area */}

        <div className="mp-right-panel">

        {/* Oyuncu ilerleme + oda kodu */}
        <div className="mp-players-bar">
          {playerList.map(([pid, p]) => (
            <div key={pid} className={`mp-player-chip ${p.is_finished ? 'finished' : ''} ${pid === playerId ? 'me' : ''} ${offlinePlayers.has(pid) ? 'offline' : ''}`}>
              <span className="mp-player-name" style={{ color: offlinePlayers.has(pid) ? 'var(--text-secondary)' : p.color }}>{p.nickname}{offlinePlayers.has(pid) ? ' ·' : ''}</span>
              <div className="mp-player-progress">
                <div className="mp-player-fill" style={{ width: `${p.progress}%`, background: p.color }}></div>
              </div>
              <span className="mp-player-pct">
                <span className="mp-player-score" style={{ color: p.color }}>{p.score ?? 0}p</span>
                <span className="mp-player-pct-num" style={{ color: p.color }}> {p.progress.toFixed(0)}%</span>
              </span>
            </div>
          ))}
        </div>

        {/* Numpad — geniş ekranda sağ panelde 3x3, mobilde gizli */}
        <Numpad
          board={gb.board}
          highlightDigit={gb.highlightDigit}
          onNumberClick={handleNumpadClick}
          className="mp-numpad-desktop"
        />

        <div className="game-controls">
          <div className="action-buttons">
            <div className="action-row action-row-play">
              <button
                className={`action-btn ${gb.noteMode ? 'active' : ''}`}
                onClick={() => gb.setNoteMode(!gb.noteMode)}
                disabled={gb.showCandidates}
                title={gb.noteMode ? t.game.noteOn : t.game.noteOff}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg></span>
                <span className="btn-label">{gb.noteMode ? t.game.noteOn : t.game.noteOff}</span>
              </button>
              <button className="action-btn" onClick={() => eraseCell(-1, -1)} title={t.game.eraseBtn}>
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7L3 16l13-13 4 4-9.5 9.5"/><path d="M6 17l-3-3"/></svg></span>
                <span className="btn-label">{t.game.eraseBtn}</span>
              </button>
              <button className="action-btn hint-btn" onClick={getHint} disabled={gb.hintsUsed >= MAX_HINTS || !allowHints || gb.mistakes >= MAX_MISTAKES || !!myState?.is_failed} title={t.game.hintBtn}>
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span>
                <span className="btn-label">{t.game.hintBtn}</span>
                <span className="hint-badge">{MAX_HINTS - gb.hintsUsed}/{MAX_HINTS}</span>
              </button>
              <button
                className={`action-btn ${gb.showCandidates ? 'active' : ''}`}
                onClick={toggleCandidates}
                disabled={!allowCandidates}
                title={gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></span>
                <span className="btn-label">{gb.showCandidates ? t.game.hideCandidates : t.game.showCandidates}</span>
              </button>
            </div>
            <div className="action-row action-row-end">
              <button
                className="action-btn solver-link"
                disabled={usedSolver}
                onClick={async () => {
                  const choice = await showChoice(
                    (t.game as any).helpMenuMsgMulti,
                    t.game.helpMenuTitle,
                    t.game.explainStepBtn,
                    t.game.fullAnalyzeBtn,
                    t.common.cancel,
                  );
                  if (choice === 'confirm') { explainStep(); return; }
                  if (choice !== 'extra') return;
                  sessionStorage.setItem(`solver_used_${roomId}`, '1');
                  setUsedSolver(true);
                  socket?.emit('player_used_solver', { room_id: roomId, player_id: playerId });
                  const boardStr = gb.board.map(row =>
                    row.map(c => (c != null && c !== 0) ? c : 0).join(',')
                  ).join(';');
                  navigate(`/solver?board=${encodeURIComponent(boardStr)}`);
                }}
                title={usedSolver ? t.game.analyzeWarningMulti : t.game.analyzeTooltip}
              >
                <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                <span className="btn-label">{usedSolver ? t.room.solverUsedLabel : t.game.analyzeWithSolver}</span>
              </button>
              {isHost && (
                <button
                  className="action-btn"
                  onClick={() => {
                    socket?.emit('restart_game', { room_id: roomId, player_id: playerId });
                    setStartingGame(true);
                    setTimeout(() => setStartingGame(false), 15000);
                  }}
                  disabled={startingGame}
                >
                  <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span>
                  <span className="btn-label">{t.room.restartGame}</span>
                </button>
              )}
              {isHost && (
                <button
                  className="action-btn danger-btn"
                  onClick={async () => {
                    const ok = await showConfirm(t.room.forceEndConfirm);
                    if (ok) {
                      socket?.emit('force_end_game', { room_id: roomId, player_id: playerId });
                    }
                  }}
                >
                  <span className="btn-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/></svg></span>
                  <span className="btn-label">{t.room.forceEndGame}</span>
                </button>
              )}
            </div>
          </div>
          <div className="game-play-ad"><AdBanner slot={AD_SLOTS.gameEnd} /></div>
        </div>{/* /game-controls */}

        </div>{/* /mp-right-panel */}
        </div>{/* /mp-panels-row */}

        {/* Canlı rekabet tablosu */}
        <div className="mp-competition-table-wrap">
          <h3 className="mp-competition-title">{t.room.competitionTableTitle}</h3>
          <div className="mp-competition-scroll">
            <table className="mp-competition-table">
              <colgroup>
                <col />{/* Sıra */}
                <col />{/* Oyuncu */}
                <col />{/* Doğru */}
                <col />{/* Yanlış */}
                <col />{/* İpucu */}
                <col />{/* Puan */}
                <col />{/* İlerleme */}
                <col />{/* Durum */}
              </colgroup>
              <thead>
                <tr>
                  <th>{t.room.rankLabel}</th>
                  <th>{t.room.playerLabel}</th>
                  <th>{t.room.correctLabel}</th>
                  <th>{t.room.wrongLabel}</th>
                  <th>{t.room.hintsLabel}</th>
                  <th>{t.room.scoreLabel}</th>
                  <th>{t.room.progress}</th>
                  <th>{t.room.statusLabel}</th>
                </tr>
              </thead>
              <tbody>
                {standings.map(([pid, p], idx) => (
                  <tr key={pid} className={`${pid === playerId ? 'is-me' : ''} ${offlinePlayers.has(pid) ? 'offline' : ''}`}>
                    <td>#{idx + 1}</td>
                    <td className="player-cell" style={{ color: p.color }}>
                      {p.nickname}
                    </td>
                    <td style={{ color: p.color }}>{p.correct_count ?? Math.round((p.progress / 100) * (81 - (gb.puzzle.flat().filter(v => v != null && v !== 0).length)))}</td>
                    <td style={{ color: p.color }}>{p.wrong_count ?? p.mistakes ?? 0}</td>
                    <td style={{ color: p.color }}>{p.hints_used ?? 0}</td>
                    <td className="score-cell" style={{ color: p.color }}>{p.score ?? 0}</td>
                    <td style={{ color: p.color }}>{(p.progress ?? 0).toFixed(0)}%</td>
                    <td>
                      <span className={`status-pill ${p.is_finished ? 'finished' : p.mistakes >= 3 ? 'failed' : p.solver_used ? 'solver-used' : offlinePlayers.has(pid) ? 'offline' : 'playing'}`}>
                        {p.is_finished ? t.room.finishedLabel : p.mistakes >= 3 ? t.room.failedLabel : p.solver_used ? t.room.solverUsedLabel : offlinePlayers.has(pid) ? t.room.offlineLabel : t.room.playingLabel}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>{/* /mp-competition-table-wrap */}


        </div>{/* /mp-top-row */}
      </div>{/* /game-layout */}
    </div>
  );
}
